

import { useState, useEffect } from "react";
import "./Payment.css";
import { FaWallet, FaCreditCard, FaBuilding, FaUser, FaDownload, FaFilter, FaTimes, FaPlus, FaHistory, FaRupeeSign, FaCalendarAlt, FaClock } from "react-icons/fa";
import axios from "axios";

const Payment = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [latestTransaction, setLatestTransaction] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [wallet, setWallet] = useState({
    totalBalance: 0,
    cashbackAmount: 0,
    companyLoadedAmount: 0,
    selfLoadedAmount: 0,
    postPaidUsage: 0,
  });

  const storedUserId = localStorage.getItem("userId");
  const userId = storedUserId ? parseInt(storedUserId, 10) : null;

  const handleOpen = () => setShowPopup(true);
  const handleClose = () => {
    setShowPopup(false);
    setAmount("");
    setIsProcessing(false);
  };

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch wallet data
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId) return;

    const fetchWallet = async () => {
      try {
        const response = await axios.get(
          `https://backend.maamaas.com/subscription/api/user/wallet/${userId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        setWallet(response.data);
      } catch (error) {
        console.error("Error fetching wallet data:", error);
      }
    };

    fetchWallet();
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        if (!userId || !token) return;

        const response = await axios.get(
          `https://backend.maamaas.com/subscription/api/user/getprofile/${userId}`,
          {
            headers: {
              accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setUserProfile(response.data);
      } catch (error) {
        console.error("❌ Failed to fetch user profile", error);
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch transactions
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId) return;

    axios
      .get(
        `https://backend.maamaas.com/subscription/api/user/wallet/transactions/${userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      )
      .then((res) => {
        const data = res.data || [];
        setTransactions(data);

        const sorted = [...data].sort(
          (a, b) => new Date(b.transactionTime) - new Date(a.transactionTime)
        );

        setLatestTransaction(sorted[0]);
      })
      .catch((err) => {
        console.error("Error fetching transactions:", err);
      });
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

const handleSubmit = async () => {
    if (!amount || amount < 1) {
        alert("Please enter a valid amount (minimum ₹1)");
        return;
    }

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId || !token) {
        alert("Please login again to continue");
        return;
    }

    setIsProcessing(true);

    try {
        const isScriptLoaded = await loadRazorpayScript();
        if (!isScriptLoaded) {
            alert("Failed to load payment gateway");
            setIsProcessing(false);
            return;
        }

        // ✅ Amount ko waise hi rakho (multiply mat karo)
        const amountInRupees = parseFloat(amount);
        
        if (isNaN(amountInRupees) || amountInRupees < 1) {
            alert("Minimum amount is ₹1");
            setIsProcessing(false);
            return;
        }

        console.log("💰 Amount in Rupees:", amountInRupees);

        // Create order on backend
        const orderResponse = await axios.post(
            "https://backend.maamaas.com/subscription/api/user/create-order",
            {
                amount: amountInRupees, // ✅ Send in rupees, not paise
                currency: "INR",
                userId: userId
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
            }
        );

        console.log("✅ Order Response:", orderResponse.data);

        const { orderId, amount: orderAmount, currency } = orderResponse.data;
        
        if (!orderId) {
            alert("Failed to create order. Please try again.");
            setIsProcessing(false);
            return;
        }

        const options = {
            key: "rzp_live_RU5whSMu9rPV7s",
            amount: orderAmount || amountInRupees, // ✅ Use as is
            currency: currency || "INR",
            name: "Maamaa's House",
            description: `Add ₹${amountInRupees} to Wallet`,
            order_id: orderId,
            prefill: {
                name: userProfile?.userName || "",
                email: userProfile?.emailId || "",
                contact: userProfile?.mobileNumber || "",
            },
            notes: {
                userId: userId,
                amount: amountInRupees
            },
            theme: {
                color: "#ee6521"
            },
            modal: {
                ondismiss: function() {
                    setIsProcessing(false);
                    alert("Payment cancelled");
                }
            },
            handler: async function(response) {
                const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = response;
                
                if (!razorpay_payment_id || !razorpay_order_id) {
                    alert("Payment verification failed");
                    setIsProcessing(false);
                    return;
                }

                try {
                    const verifyResponse = await axios.post(
                        "https://backend.maamaas.com/subscription/api/user/verify-payment",
                        {
                            paymentId: razorpay_payment_id,
                            orderId: razorpay_order_id,
                            signature: razorpay_signature,
                            userId: userId,
                            amount: amountInRupees // ✅ Send in rupees
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`
                            },
                        }
                    );

                    if (verifyResponse.data.success) {
                        await axios.post(
                            `https://backend.maamaas.com/subscription/api/user/addCash/self-loaded`,
                            {},
                            {
                                params: {
                                    userId: userId,
                                    amount: amountInRupees, // ✅ Send in rupees
                                    paymentId: razorpay_payment_id,
                                    orderId: razorpay_order_id
                                },
                                headers: { 
                                    Authorization: `Bearer ${token}`,
                                    "Content-Type": "application/json"
                                },
                            }
                        );

                        alert(`Successfully added ₹${amountInRupees} to your wallet!`);
                        
                        // Refresh wallet
                        const walletResponse = await axios.get(
                            `https://backend.maamaas.com/subscription/api/user/wallet/${userId}`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );
                        setWallet(walletResponse.data);
                        
                        const transactionsResponse = await axios.get(
                            `https://backend.maamaas.com/subscription/api/user/wallet/transactions/${userId}`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );
                        setTransactions(transactionsResponse.data || []);
                        
                        handleClose();
                    } else {
                        alert("Payment verification failed");
                    }
                } catch (error) {
                    console.error("Payment verification error:", error);
                    alert("Payment verification failed");
                } finally {
                    setIsProcessing(false);
                }
            }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        
    } catch (error) {
        console.error("Payment error:", error);
        alert("Failed to initialize payment");
        setIsProcessing(false);
    }
};

  const openTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  // Pagination
  const rowsPerPage = isMobile ? 5 : 10;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const sortedTransactions = [...transactions].sort(
    (a, b) => new Date(b.transactionTime) - new Date(a.transactionTime)
  );
  const currentTransactions = sortedTransactions.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(transactions.length / rowsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'CREDIT': return '🟢';
      case 'DEBIT': return '🔴';
      default: return '⚪';
    }
  };

  return (
    <div className="payment-container">
      {/* Wallet Cards Section */}
      <div className="wallet-cards">
        <div className="wallet-card total-balance">
          <div className="card-content">
            <h3 className="card-title">Total Balance</h3>
            <p className="card-amount">₹{wallet.totalBalance}</p>
          </div>
        </div>

        <div className="wallet-card cashback">
          <div className="card-content">
            <h3 className="card-title">Cashback</h3>
            <p className="card-amount">₹{wallet.cashbackAmount}</p>
          </div>
        </div>

        <div className="wallet-card company">
          <div className="card-content">
            <h3 className="card-title">Company Loaded</h3>
            <p className="card-amount">₹{wallet.companyLoadedAmount}</p>
          </div>
        </div>

        <div className="wallet-card self">
          <div className="card-content">
            <h3 className="card-title">Self Loaded</h3>
            <p className="card-amount">₹{wallet.selfLoadedAmount}</p>
          </div>
        </div>
        
        <div className="wallet-card Post">
          <div className="card-content">
            <h3 className="card-title">Post Loaded</h3>
            <p className="card-amount">₹{wallet.postPaidUsage}</p>
          </div>
        </div>

        <div className="wallet-card load-wallet">
          <div className="card-content">
            <h3 className="card-title">Load Wallet</h3>
            <button className="load-btn" onClick={handleOpen} disabled={isProcessing}>
              <FaPlus />
              Add Money
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Section */}
      <div className="transactions-section">
        <div className="section-header">
          <div className="section-title">
            <FaHistory />
            <h2>Transaction History</h2>
          </div>
        </div>

        {isMobile ? (
          /* Mobile Cards View */
          <div className="transaction-cards">
            {currentTransactions.length > 0 ? (
              currentTransactions.map((transaction, index) => (
                <div 
                  key={transaction.id || index} 
                  className="transaction-card"
                  onClick={() => openTransactionDetails(transaction)}
                >
                  <div className="transaction-header">
                    <div className="transaction-type">
                      <span className="type-icon">
                        {getTransactionIcon(transaction.transactionType)}
                      </span>
                      <span className="type-text">{transaction.transactionType}</span>
                    </div>
                    <div 
                      className={`transaction-amount ${transaction.transactionType === 'CREDIT' ? 'credit' : 'debit'}`}
                    >
                      {transaction.transactionType === 'CREDIT' ? '+' : '-'}₹{transaction.amount}
                    </div>
                  </div>

                  <div className="transaction-details">
                    <div className="detail-row">
                      <span className="label">Balance Type:</span>
                      <span className="value">{transaction.balanceType}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Date:</span>
                      <span className="value">
                        <FaCalendarAlt /> {formatDate(transaction.transactionTime)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Time:</span>
                      <span className="value">
                        <FaClock /> {formatTime(transaction.transactionTime)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>No Transactions Found</h3>
                <p>Your transaction history will appear here</p>
              </div>
            )}
          </div>
        ) : (
          /* Desktop Table View */
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date & Time</th>
                  <th>Transaction Type</th>
                  <th>Amount</th>
                  <th>Balance Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.map((transaction, index) => (
                  <tr key={transaction.id || index}>
                    <td className="serial-number">{indexOfFirstRow + index + 1}</td>
                    <td className="datetime-cell">
                      <div className="date-time">
                        <div className="date">
                          <FaCalendarAlt /> {formatDate(transaction.transactionTime)}
                        </div>
                        <div className="time">
                          <FaClock /> {formatTime(transaction.transactionTime)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`transaction-type-badge ${transaction.transactionType.toLowerCase()}`}>
                        {transaction.transactionType}
                      </span>
                    </td>
                    <td className={`amount-cell ${transaction.transactionType === "CREDIT" ? "credit" : "debit"}`}>
                      {transaction.transactionType === "CREDIT" ? "+" : "-"}₹{transaction.amount}
                    </td>
                    <td className="balance-type">{transaction.balanceType}</td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => openTransactionDetails(transaction)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {transactions.length > rowsPerPage && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn prev"
            >
              Previous
            </button>
            <div className="page-info">
              Page {currentPage} of {totalPages}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn next"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Load Wallet Modal */}
      {showPopup && (
        <div className="modal-overlay">
          <div className="modal-contenty">
            <div className="modal-header">
              <h2>Load Wallet</h2>
              <button className="close-btn" onClick={handleClose}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Enter Amount (Min ₹1)</label>
                <div className="amount-input">
                  <FaRupeeSign className="input-icon" />
                  <input
                    type="number"
                    placeholder="Enter amount to load"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="1"
                    step="1"
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <div className="quick-amounts">
                <span>Quick Select:</span>
                <div className="amount-buttons">
                  {[100, 200, 500, 1000, 2000].map(quickAmount => (
                    <button
                      key={quickAmount}
                      className="quick-amount-btn"
                      onClick={() => setAmount(quickAmount.toString())}
                      disabled={isProcessing}
                    >
                      ₹{quickAmount}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="submit-btn"
                onClick={handleSubmit}
                disabled={!amount || amount < 1 || isProcessing}
              >
                {isProcessing ? "Processing..." : "Proceed to Pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {isModalOpen && selectedTransaction && (
        <div className="modal-overlay">
          <div className="modal-contenty">
            <div className="modal-header">
              <h2>Transaction Details</h2>
              <button className="close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="transaction-detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">{selectedTransaction.id || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className={`detail-value type-badge ${selectedTransaction.transactionType.toLowerCase()}`}>
                    {selectedTransaction.transactionType}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Amount:</span>
                  <span className={`detail-value amount ${selectedTransaction.transactionType === 'CREDIT' ? 'credit' : 'debit'}`}>
                    {selectedTransaction.transactionType === 'CREDIT' ? '+' : '-'}₹{selectedTransaction.amount}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Balance Type:</span>
                  <span className="detail-value">{selectedTransaction.balanceType}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">
                    <FaCalendarAlt /> {formatDate(selectedTransaction.transactionTime)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Time:</span>
                  <span className="detail-value">
                    <FaClock /> {formatTime(selectedTransaction.transactionTime)}
                  </span>
                </div>
              </div>

              <div className="transaction-description">
                <h4>Description</h4>
                <p>
                  This {selectedTransaction.transactionType.toLowerCase()} transaction was successfully processed. 
                  {selectedTransaction.transactionType === 'CREDIT' 
                    ? ' The amount has been added to your wallet balance.' 
                    : ' The amount has been deducted from your wallet balance.'
                  }
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="close-details-btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;